import { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { GetVideo } from "../../../../Api/Video";
import VideoDTO from "../../../../Models/DTOs/VideoDTO";
import { UserAuthenticationContext } from "../../../../Context/UserAuthenticationContext";

function useFetchVideo(id: number | undefined, countryCode: string) {
    const auth = useContext(UserAuthenticationContext);
    const authRef = useRef(auth);
    authRef.current = auth;
    const [video, setVideo] = useState<VideoDTO | null>(null);
    const [videoLoading, setVideoLoading] = useState(false);
    const [videoError, setVideoError] = useState("");
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => setAttempt(value => value + 1), []);

    useEffect(() => {
        const abortController = new AbortController();
        setVideo(null);
        setVideoError("");
        if (!id || authRef.current.authInitializing) {
            setVideoLoading(!!id);
            return () => abortController.abort();
        }
        setVideoLoading(true);
        void (async () => {
            try {
                const currentAuth = authRef.current;
                let token = currentAuth.doesAuthTokenExist?.() ? await currentAuth.getValidAccessToken() : null;
                if (abortController.signal.aborted) return;
                let data: VideoDTO;
                try {
                    data = await GetVideo(id, abortController, token);
                } catch (error) {
                    if (!token || !axios.isAxiosError(error) || error.response?.status !== 401) throw error;
                    const refreshed = await currentAuth.refreshTokens();
                    token = refreshed ? await currentAuth.getValidAccessToken() : null;
                    if (abortController.signal.aborted) return;
                    if (!token) throw error;
                    data = await GetVideo(id, abortController, token);
                }
                if (!abortController.signal.aborted) setVideo(data);
            } catch {
                if (!abortController.signal.aborted) setVideoError("Failed to load this video.");
            } finally {
                if (!abortController.signal.aborted) setVideoLoading(false);
            }
        })();
        return () => abortController.abort();
    }, [id, countryCode, auth.authInitializing, auth.authSessionVersion, auth.userData?.id, attempt]);

    return { video: video?.Id === id ? video : null, videoLoading, videoError, retry };
}
export default useFetchVideo;
